import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { Box, Button, FormControl, FormGroup, MenuItem, Select, styled, Switch, TextField, IconButton, FormControlLabel, RadioGroup, Radio, Grid, ClickAwayListener, Tooltip, Checkbox } from '@mui/material';
import swal from 'sweetalert';
import debounce from "lodash.debounce";
import GooglePlacesAutocomplete from 'react-google-autocomplete';
import { ApiKey } from '../utils/UtilityFunctions';
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import i18next from 'i18next';
import moment from 'moment/moment';
import dayjs from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import codes from 'country-calling-code'
// import FormQuestions from './FormQuestions';

const FormQuestions = ({element, handleFormValues, formValues, formQuestions, doctorProfile, from}) => {

  const numRef = useRef(null)
  const autocompleteRef = useRef(null);
  const [addressFound, setAddressFound] = useState(true);
  const [image, setImage] = useState(null);
  const [date, setDate] = useState('')
  const [radioVal, setRadioVal] = useState('')
  const [depQuestionsList, setDepQuestionsList] = useState([])
  const [tooltip, setTooltip] = useState(false)
  const [documentType] = useState([
    'Passport',
    'National ID',
  ])
  const [checkboxOptions, setCheckboxOptions] = useState({});
  const [countrySearch, setCountrySearch] = useState('');

  useEffect(() => {
    element?.commonIdentifier === 'signature_base64' && setImage(element?.value)
  }, [])

  useEffect(() => {
    if(element?.value){
      handleFormValues(element?.value, element.commonIdentifier)
    }
  }, [element?.value])

  // Filter countries based on search term
  const filteredCountries = useMemo(() => {
    if (!countrySearch.trim()) {
      return codes;
    }

    const searchLower = countrySearch.toLowerCase().trim();
    return codes.filter((country) => {
      const countryName = country.country.toLowerCase();
      const countryCode = country.countryCodes[0].toString();

      // Match by country name or calling code (with or without +)
      return countryName.includes(searchLower) ||
             countryCode.includes(searchLower) ||
             (`+${countryCode}`).includes(searchLower);
    });
  }, [countrySearch]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0]; // Optional chaining for safety
    if (!file) return;

    const reader = new FileReader();

    reader.onloadend = () => {
      if (reader.result) {
        const base64Image = reader.result; // Base64 string
        setImage(base64Image);
        handleFormValues(base64Image.split(",")[1], "signature", 'newupload');
      } else {
        console.error("FileReader failed to load the file.");
      }
    };

    reader.onerror = (err) => {
      console.error("FileReader error: ", err);
    };

    reader.readAsDataURL(file); // Convert to Base64
  };

  const handleRemoveImage = () => {
    setImage(null);
    if(formValues.signature != ''){
      handleFormValues('', 'signature_base64')
      element.value = null
    }
  };

  const handlePlaceSelected = useCallback(
    debounce((place) => {
      if (!place || !place.geometry || !place.address_components) return;

      const location = place.geometry.location;
      const lat = location.lat();
      const lng = location.lng();
       // Extract city, postal code, and country
      let city = '';
      let postalCode = '';
      let country = '';
      let state = '';
      place.address_components.forEach((component) => {
        const types = component.types;
        if (types.includes('locality')) city = component.long_name;
        if (types.includes('postal_code')) postalCode = component.long_name;
        if (types.includes('country')) country = component.long_name;
        if (types.includes('administrative_area_level_1')) state = component.long_name;
      });

      // console.log("Selected place:", place);
      // console.log("Lat:", lat, "Lng:", lng);
      // console.log("City:", city, "Postal Code:", postalCode, "State:", state, "Country:", country);
      handleFormValues({
        address: place.formatted_address, 
        lat: lat, 
        lng: lng, 
        city: city, 
        postalCode: postalCode, 
        country: country
      }, "c_address");
    }, 2000),
    [] // only create debounced function once
  );

  const handleRadioChange = (e, ele) => { 
    const value = e?.target?.value?.toUpperCase() || "";
    const identifier = ele?.commonIdentifier;

    const yesValues = ["YES", "OUI", "SÍ"];
    const noneValues = ["NONE", "AUCUNE", "AUCUN", "NINGUNA", "NO, I HAVE NOT REACHED MENOPAUSE", "NON, JE N’AI PAS ENCORE ATTEINT LA MÉNOPAUSE", "NO, NO HE ALCANZADO LA MENOPAUSIA"];
    const medicalConditions = ["PROSTATE CANCER", "SEVERE LIVER FAILURE", "CANCER DE LA PROSTATE", "INSUFFISANCE HÉPATIQUE GRAVE", ""];

    if (
      (identifier === "psychological_state" && yesValues.includes(value)) ||
      (identifier === "scalp_conditions" && value && !noneValues.includes(value)) ||
      (identifier === "medical_history" && medicalConditions.includes(value)) ||
      (identifier === "menopause_state" && noneValues.includes(value)) ||
      (identifier === "hormone_sensitive_breast_cancer" && yesValues.includes(value)) ||
      (identifier === "dutasteride_allergy" && yesValues.includes(value)) ||
      (identifier === "depression_or_dosage_of_antidepressants" && yesValues.includes(value))
    ) {
      swal({
        title: i18next.t('We regret to inform you that we are unable to offer you treatment at this time as a result of one or more of your responses. If this was a mistake, please unselect the incorrect answers and try again.'),
        icon: 'warning'
      })
    } else {
      setRadioVal(e.target.value)
      handleFormValues(e?.target?.value, ele?.commonIdentifier)
      let dependentList =
        ele.commonIdentifier && ele.commonIdentifier === 'hair_loss_treatment' && e.target.value != 'No' ? 
          element?.dependentQuestions?.length > 0 &&
          element?.dependentQuestions.map((obj) => {
            if(obj.selectedOption.includes(e?.target?.value)) {
              obj['selectedParent'] = e.target.value
              return obj;
            }
          }) 
            :
          element?.dependentQuestions?.length > 0 &&
          element?.dependentQuestions.filter((obj) =>
            obj.selectedOption.includes(e?.target?.value)
          );
      setDepQuestionsList(dependentList);
    }
  }

  const handleCheckboxChange = (optionKey, optionValue, checked, ele) => {
    const updatedSelections = { ...checkboxOptions };

    if (checked) {
      updatedSelections[optionValue] = optionValue;
    } else {
      delete updatedSelections[optionValue];
    }

    setCheckboxOptions(updatedSelections);
    console.log("updatedSelections", updatedSelections);
    
    // Send updated selection to parent
    if (handleFormValues) {
      handleFormValues(updatedSelections, ele?.commonIdentifier)
    }
  };

  const callSameComponent = () => (
    depQuestionsList?.length > 0 && depQuestionsList.map((ele, idx) => (
      <Grid item xs={12}>
        <FormQuestions 
          key={idx}
          element={ele} 
          handleFormValues={handleFormValues}
          formQuestions={formQuestions}
          formValues={formValues}
        />
      </Grid>
    ))
  )

  const openTooltip = () => {
    setTooltip((prev) => !prev);
  };

  const closeTooltip = () => {
    setTooltip(false);
  };

  const renderQuestion = (element) => {
    if(from && (from === "alqi" || from === "satisfaction")) {
      return (
        <Box className='quesDiv' sx={{ textAlign: 'left' }}>
            <Box className='quesTitle' sx={{ pb: 1 }}>    
            {element?.title}
            </Box>
            <div className={element?.value ? 'quesInput filled' : 'quesInput'}>
            {getQuestionType(element)}
            </div>
        </Box>
      )
    } else {
      return (
        <div className='quesDiv'>
          {
            element?.type === 'address'? 
              <>
                <div className='quesTitle addressTitle'>
                  {element?.title}
                  <FormControlLabel
                    className='addressSwitch'
                    control={<Switch checked={addressFound} onChange={(e) => setAddressFound(e.target.checked)}/>}
                  />
                </div> 
              </> 
                :
            doctorProfile && 
            ( 
              element.commonIdentifier === 'clinic_name' || 
              element.commonIdentifier === 'clinic_email' || 
              element.commonIdentifier === 'phone_number'
            ) ?
              <div className='quesTitle'>
                {element?.title}
              </div>
                :
            // (element?.commonIdentifier === 'phone' || element?.commonIdentifier === 'email') ?
            //   <>
            //     <div className='quesTitle'>
            //       {element?.title}
            //     </div> 
            //   </> 
            //     :
            // element.commonIdentifier === 'passport' ?
            //   <>
            //     <div className='quesTitle'>
            //       {element?.title}<span className='ml-5px mandatory'>*</span><span className='ml-5px quesTitleSub'>{"(For prescription)"}</span>
            //     </div> 
            //   </>
            //     :
            <div className='quesContainer'>
              <div className='quesTitle'>
                {element?.title}
                <span className='ml-5px mandatory'>*</span>
                <span className='tooltipspan ml-5px'>
                  {
                    element?.note && element.note &&
                      <ClickAwayListener onClickAway={closeTooltip}>
                        <div className='toolTipDiv'>
                          <Tooltip
                            PopperProps={{
                              disablePortal: true,
                            }}
                            onClose={closeTooltip}
                            open={tooltip}
                            disableFocusListener
                            disableHoverListener
                            disableTouchListener
                            placement='bottom'
                            title={
                              <div>
                                <div className='d-flex justify-content-end align-items-end mb-5px'>
                                  <CloseIcon onClick={closeTooltip}/>  
                                </div>
                                <p>{element?.note}</p>
                              </div>
                            }
                          >
                            <InfoIcon onClick={openTooltip}/>
                          </Tooltip>
                        </div>
                      </ClickAwayListener>
                  }
                </span>
              </div>  
            </div>
          }
          <div className={element?.value ? 'quesInput filled' : 'quesInput'}>
            {getQuestionType(element)}
          </div>
          {callSameComponent()}
        </div>
      )
    }
  }

  const getQuestionType = (element) => {
    switch (element?.type) {
      case "text":
        return getTextType(element);
      case "number":
        return getNumberType(element);
      case "dropdown":
        return getDropdownType(element);
      case "radio":
        return getRadioType(element);
      case "checkbox":
        return getCheckboxType(element);
      case "address":
        return getAddressType(element);
      case "date":
        return getDateType(element);
      case "alphanumeric":
        return getAlphanumericType(element);
      case "email":
        return getEmailType(element);
      case "signature":
        return getSignatureType(element);
      case "signature_base64":
        return getSignatureType(element);
      default:
        return getTextType(element);
    }
  };
  
  const getTextType = (element) => {
    return (
      <TextField 
        placeholder={element?.placeholder} 
        variant="outlined"
        onChange={(e) => handleFormValues(e.target.value, element?.commonIdentifier, element)}
        defaultValue={element?.value && element.value}
        disabled={
          doctorProfile && (element?.commonIdentifier === 'clinic_name') && true
        }
      />
    );
  };
  
  const getNumberType = (element) => {
    return (
      <TextField
        defaultValue={element?.value && element.value}
        // inputRef={numRef}
        placeholder={element?.placeholder} 
        variant="outlined"
        inputProps={{
          maxLength: element?.commonIdentifier === 'phone' || element?.commonIdentifier === 'phone_number' ? 15 : 3,
          minLength: element?.commonIdentifier === 'phone' || element?.commonIdentifier === 'phone_number' ? 5 : 1,
          inputMode: 'numeric',       // Brings up number keyboard on mobile
          pattern: '[0-9]*',          // Accept only digits
        }}
        disabled={
          doctorProfile && (element.commonIdentifier === 'phone_number') && true
        }
        onInput={(e) => {
          e.target.value = e.target.value.replace(/[^0-9]/g, '');
        }}
        onChange={(e) => handleFormValues(e.target.value, element?.commonIdentifier)}
        onBlur={() => {
          if((element?.commonIdentifier === 'phone' || element?.commonIdentifier === 'phone_number') && formValues.phone !== "" && formValues.phone.length < 3){
            swal({
              title: "Please enter a valid phone number",
              icon: "warning",
            }).then(() => {
              numRef?.current?.focus(); // Re-focus after alert
            });
          } else if(element?.commonIdentifier === 'age' && (formValues.age !== "" && formValues.age < 1 || formValues.age > 120)){
            swal({
              title: "Please enter a valid age",
              icon: "warning",
            }).then(() => {
              numRef?.current?.focus(); // Re-focus after alert
            });
          }
        }}
      />
    );
  }

  const getDropdownType = (element) => {
    if(element.commonIdentifier === "document_issue_country" || element.commonIdentifier === "document_type") {
      return (
        <Select
          id="demo-simple-select"
          // value={element.commonIdentifier === "document_issue_country" ? formValues?.document_issue_country : formValues?.document_type}
          value={formValues?.[element?.commonIdentifier] || ''}
          onChange={(e) => handleFormValues(e.target.value, element?.commonIdentifier)}
          // placeholder='Please select'
          displayEmpty
          renderValue={(selected) => {
            if (selected === "" || !selected) {
              return <span style={{ color: '#aaa' }}>{i18next.t(element.commonIdentifier === "document_issue_country" ? "Select Issuing Country" : "Select Document Type")}</span>;
            }
            return selected;
          }}
          className='countrySelect'
          onOpen={() => element.commonIdentifier === "document_issue_country" && setCountrySearch('')}
          MenuProps={element.commonIdentifier === "document_issue_country" ? {
            autoFocus: false,
            PaperProps: {
              style: {
                maxHeight: 400,
              },
            },
          } : {}}
        >
          {element.commonIdentifier === "document_issue_country" && (
            <MenuItem
              disableRipple
              onKeyDown={(e) => e.stopPropagation()}
              style={{
                position: 'sticky',
                top: 0,
                backgroundColor: 'white',
                zIndex: 1,
                paddingTop: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #e0e0e0'
              }}
            >
              <TextField
                autoFocus
                placeholder={i18next.t("Search country...")}
                fullWidth
                size="small"
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </MenuItem>
          )}
          <MenuItem value="" disabled>
            {i18next.t('Please select')}
          </MenuItem>
          {
            element.commonIdentifier === "document_issue_country" ?
              filteredCountries.map((o, i) => {
                return (
                  <MenuItem value={o.country} key={i}>
                    {o.country}
                  </MenuItem>
                );
              })
                :
              documentType.map((o, i) => {
                return (
                  <MenuItem value={o} key={i}>
                    {o}
                  </MenuItem>
                );
              })
          }
          {element.commonIdentifier === "document_issue_country" && filteredCountries.length === 0 && (
            <MenuItem disabled style={{ justifyContent: 'center' }}>
              {i18next.t("No countries found")}
            </MenuItem>
          )}
        </Select>
      )
    } else {
      return (
        <Select
          id="demo-simple-select"
          className='genderSelect'
          value={formValues?.[element?.commonIdentifier] || ''}
          onChange={(e) => handleFormValues(e.target.value, element?.commonIdentifier)}
          displayEmpty
          renderValue={(selected) => {
            if (selected === "" || !selected) {
              return <span style={{ color: '#aaa' }}>{i18next.t('Please select')}</span>;
            }
            return selected;
          }}
        >
          <MenuItem value="" disabled>
            {i18next.t('Please select')}
          </MenuItem>
          {
            element?.optionMap.map((o, index) => (
              <MenuItem key={index} value={o}>
                {o}
              </MenuItem>
            ))
          }
        </Select>
      )
    }
  }

  const getRadioType = (element) => {
    return(
      <RadioGroup
        onChange={(e) => handleRadioChange(e, element)}
        value={radioVal}
        row={from && from === "satisfaction" && true}
      >
        {
          Object.keys(element?.optionMap).length > 0 && Object.keys(element?.optionMap).map((k) => {
            return(
              <FormControlLabel 
                key={k}
                value={element.optionMap[k]} 
                control={<Radio/>} 
                label={element.optionMap[k]}
              />
            )
          })
        }
      </RadioGroup>
    )  
  }

  const getCheckboxType = (element) => {
    return(
      <FormGroup>
        {
          element?.optionMap && Object.keys(element?.optionMap).map((option, idx) => {
            const optionValue = element.optionMap[option];
            return(
              <FormControlLabel
                key={idx}
                control={
                  <Checkbox
                    checked={!!checkboxOptions[optionValue]}
                    onChange={(e) => 
                      handleCheckboxChange(option, element.optionMap[option], e.target.checked, element)
                    }
                  />
                }
                label={element?.optionMap[option]}
              />
            )
          })
        }
      </FormGroup>
    )
  }

  const getAddressType = (element) => {
    return (
      <>
        {
          !addressFound ? 
            <TextField 
              placeholder={element?.placeholder} 
              variant="outlined"
              onChange={(e) => handleFormValues(e.target.value, element?.commonIdentifier)}
            />
              :
            <div className="googlePlaceDiv">
              <GooglePlacesAutocomplete
                defaultValue={element.value && element.value}
                className="googlePlaceTxtbox commonInputText"
                apiKey={ApiKey}
                onPlaceSelected={handlePlaceSelected}
                ref={autocompleteRef}
                options={{ types: ["establishment"] }}
                placeholder="Enter a location"
                style={{
                  width: "100%",
                  height: "40px",
                  padding: "10px",
                  fontSize: "16px",
                }}
              />
            </div> 
        }
      </>
    )
  }

  const getDateType = (element) => {
    return(
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          views={['year']}
          value={date}
          onChange={(newValue) => {
            newValue && setDate(newValue)
            handleFormValues(newValue?.year(), element?.commonIdentifier)
          }}
          minDate={dayjs(moment().subtract(90, "years"))} // 90 years ago
          maxDate={dayjs(moment().subtract(18, "years"))} // 18 years ago
          renderInput={(params) => 
            <TextField 
              placeholder='Select year' 
              {...params} 
              fullWidth 
              inputProps={{
                ...params.inputProps,
                readOnly: true, // ⛔ Disable manual input
              }}
            />
          }
        />
      </LocalizationProvider>
    )
  }

  const getAlphanumericType = (element) => {
    return (
      <TextField
        defaultValue={element.value && element.value}
        // inputRef={numRef}
        placeholder={element?.placeholder} 
        variant="outlined"
        inputProps={{
          maxLength: 15,
          minLength: 5,
          // inputMode: 'numeric',       // Brings up number keyboard on mobile
          pattern: '[A-Za-z0-9]*',          // Accept only digits
        }}
        onInput={(e) => {
          e.target.value = e.target.value.replace(/[^A-Za-z0-9]/g, '');
        }}
        onChange={(e) => handleFormValues(e.target.value, element?.commonIdentifier)}
        onBlur={() => {
          if(formValues?.license_number !== "" && formValues?.license_number?.length < 6 || formValues?.license_number?.length > 15){
            swal({
              title: "Please enter a valid license number",
              icon: "warning",
            }).then(() => {
              numRef?.current?.focus(); // Re-focus after alert
            });
          } else if(formValues?.passport !== "" && formValues?.passport?.length < 6 || formValues?.passport?.length > 15){
            swal({
              title: "Please enter a valid passport number",
              icon: "warning",
            }).then(() => {
              numRef?.current?.focus(); // Re-focus after alert
            });
          } else if(formValues?.document_number !== "" && formValues?.document_number?.length < 6 || formValues?.document_number?.length > 15){
            swal({
              title: "Please enter a valid document number",
              icon: "warning",
            }).then(() => {
              numRef?.current?.focus(); // Re-focus after alert
            });
          }
        }}
      />
    );
  }

  const getEmailType = (element) => {
    return (
      <TextField
        defaultValue={element.value && element.value}
        // inputRef={numRef}
        placeholder={element?.placeholder} 
        variant="outlined"
        inputProps={{
          maxLength: 100,        // Reasonable max length for email
          inputMode: 'email',    // Optimized keyboard for email input
          type: 'email',         // Email type for native validation
        }}
        onChange={(e) => handleFormValues(e.target.value, element?.commonIdentifier)}
        onBlur={(e) => {
          const emailValue = e.target.value.trim();
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Improved regex
          if (element?.commonIdentifier === 'email' && emailValue !== "" && !emailRegex.test(emailValue)) {
            swal({
              title: "Please enter a valid email address",
              icon: "warning",
            }).then(() => {
              numRef?.current?.focus();
            });
          }
        }}
        disabled={
          doctorProfile && (element.commonIdentifier === 'clinic_email') && true
        }
      />
    );
  }

  const getSignatureType = (element) => {
    return (
      <>
        {!image && (
          <Button variant="contained" component="label" className='uploadImageBtn'>
            {i18next.t("Upload Image")}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={handleFileChange}
            />
          </Button>
        )}
        {image && (
          <div className='d-flex justify-content-start align-items-center'>
            <img
              src={image?.includes('base64') ? image : `data:image/jpeg;base64,${image}` }
              alt="Preview"
              style={{ width: 200, height: "auto", borderRadius: 8 }}
            />
            <IconButton color="error" onClick={handleRemoveImage}>
              <DeleteIcon />
            </IconButton>
          </div>
        )}
      </>
    )
  }

  return (
    <div className='formComponent w-100'>
      <FormControl className='formControl w-100'>
        <FormGroup>
          {renderQuestion(element)}
        </FormGroup>
      </FormControl>
    </div>
  )
}

export default FormQuestions